import React, { useCallback } from 'react'

import { useDispatch, useSelector } from 'react-redux'

import { useCollectionRecipeSets, useCurrentCollectionId } from 'hooks/menu-collections'
import type { RecipeUUID } from 'reducers/menu-recipes/recipes'
import { useRecipeDetailData } from 'routes/Menu/components/Detail/hooks/useRecipeDetailData'
import { getBasketDate, getBasketMenuUuid, getNumPortions } from 'selectors/basket'

import { closeRecipeDetails } from '../../../actions/closeRecipeDetails'
import {
  getMenuRecipeForDetails,
  getMenuRecipeUuidForDetails,
} from '../../../selectors/menuRecipeDetails'
import { Detail } from '../Detail'

interface DetailOverlayProps {
  showOverlay?: boolean
}

/**
 * Returns the position of the recipe in the collection recipe sets list,
 * works for cases when `recipeId` is an option in the recipe set
 *
 * If recipe is not in the collection, returns undefined
 */
const useRecipePosition = (recipeId: RecipeUUID | null): number | undefined => {
  const currentCollectionId = useCurrentCollectionId()
  const currentCollectionRecipeSets = useCollectionRecipeSets(currentCollectionId)

  if (!recipeId) {
    return undefined
  }

  const recipeIndex = currentCollectionRecipeSets.findIndex(({ recipeIds }) =>
    recipeIds.has(recipeId),
  )

  return recipeIndex >= 0 ? recipeIndex + 1 : undefined
}

const DetailOverlay = ({ showOverlay: showOverlayProp }: DetailOverlayProps) => {
  const showOverlay = Boolean(useSelector(getMenuRecipeUuidForDetails)) && showOverlayProp
  const recipeUuid = useSelector(getMenuRecipeUuidForDetails)
  const menuRecipeDetailShow = recipeUuid || ''
  const recipePosition = useRecipePosition(recipeUuid as RecipeUUID | null)

  const numPortions = useSelector(getNumPortions)
  const detailRecipe = useSelector(getMenuRecipeForDetails)
  const dispatch = useDispatch()
  const onClose = useCallback(() => dispatch(closeRecipeDetails()), [dispatch])
  const deliveryDate = useSelector(getBasketDate)
  const menuId = useSelector(getBasketMenuUuid)
  const collectionId = useCurrentCollectionId()
  const shouldFetchOptions = true

  const { loading, recipe, error, reload } = useRecipeDetailData({
    menuId,
    recipeUuid,
    numPortions,
    deliveryDate,
    shouldFetchOptions,
    collectionId,
  })

  if (!(showOverlay && detailRecipe && menuRecipeDetailShow)) {
    return null
  }

  return (
    <Detail
      uuid={detailRecipe.id}
      coreId={detailRecipe.coreId}
      title={detailRecipe.name}
      showOverlay={showOverlay}
      loading={loading}
      error={error}
      recipe={recipe}
      reload={reload}
      position={recipePosition?.toString()}
      onClose={onClose}
    />
  )
}

export { DetailOverlay }
